import * as React from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { CURRENCY_CODES, DEFAULT_CURRENCY } from "@/lib/constants"
import type { User } from "@/types/domain.types"
import { useUserDebts } from "@/hooks/queries/useUserDebts"
import { Info } from "lucide-react"
import { cn } from "@/lib/utils"
import { logger } from '@/lib/logger'

interface SettlementFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupMembers: User[]
  groupId: string
  currentUserId: string
  onSubmit: (data: {
    from_user_id: string
    to_user_id: string
    amount: number
    currency_code: string
    description?: string
  }) => Promise<void>
}

export const SettlementForm: React.FC<SettlementFormProps> = ({
  open,
  onOpenChange,
  groupMembers,
  groupId,
  currentUserId,
  onSubmit,
}) => {
  const debtsQuery = useUserDebts(groupId)
  const debtsData = debtsQuery.data
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [fromUserId, setFromUserId] = React.useState(currentUserId)
  const [toUserId, setToUserId] = React.useState("")
  const [amount, setAmount] = React.useState("")
  const [currency, setCurrency] = React.useState(DEFAULT_CURRENCY)
  const [description, setDescription] = React.useState("")
  const [settlementType, setSettlementType] = React.useState<"full" | "partial">("full")

  // Find the debt between selected users (in either direction)
  const currentDebt = React.useMemo(() => {
    if (!debtsData || !fromUserId || !toUserId) return null

    // Look for debt in either direction
    const debt = debtsData.debts.find(
      (debt) =>
        (debt.from_user_id === fromUserId && debt.to_user_id === toUserId) ||
        (debt.from_user_id === toUserId && debt.to_user_id === fromUserId)
    )

    // If we find a debt, return it with proper direction info
    if (debt) {
      // Check if the debt direction matches our settlement direction
      const isReversed = debt.from_user_id !== fromUserId
      return {
        ...debt,
        isReversed, // Track if we need to reverse the interpretation
        actualFromUser: isReversed ? debt.to_user_id : debt.from_user_id,
        actualToUser: isReversed ? debt.from_user_id : debt.to_user_id,
      }
    }

    return null
  }, [debtsData, fromUserId, toUserId])

  // Update amount when settlement type changes or users change
  React.useEffect(() => {
    if (settlementType === "full" && currentDebt) {
      // Set the full debt amount
      setAmount(currentDebt.amount.toString())
    } else if (settlementType === "partial") {
      // Clear amount for partial settlement
      setAmount("")
    }
  }, [settlementType, currentDebt])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!fromUserId || !toUserId || !amount) {
      alert("Please fill in all required fields")
      return
    }

    if (fromUserId === toUserId) {
      alert("Cannot create settlement between the same user")
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit({
        from_user_id: fromUserId,
        to_user_id: toUserId,
        amount: parseFloat(amount),
        currency_code: currency,
        description: description || undefined,
      })

      // Reset form
      setFromUserId(currentUserId)
      setToUserId("")
      setAmount("")
      setDescription("")
      onOpenChange(false)
    } catch (error) {
      logger.error("Failed to create settlement:", error)
      alert("Failed to create settlement. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Record Settlement</DialogTitle>
            <DialogDescription>
              Record a payment made to settle debts between group members
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Debt Information Alert */}
            {fromUserId && toUserId && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {currentDebt ? (
                    <>
                      <div className="font-medium mb-1">Current Debt:</div>
                      {/* Always show the actual debt relationship */}
                      <div>
                        <span className="font-semibold">{currentDebt.from_user_name}</span> owes{" "}
                        <span className="font-semibold">{currentDebt.to_user_name}</span>{" "}
                        <span className="text-destructive font-bold">
                          {DEFAULT_CURRENCY} {currentDebt.amount.toFixed(2)}
                        </span>
                      </div>
                      {/* Show settlement direction info if it's reversed */}
                      {currentDebt.isReversed && (
                        <div className="text-sm text-muted-foreground mt-1">
                          Note: This settlement will reduce the debt in the opposite direction
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-muted-foreground">
                      No outstanding debt between these users
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid gap-2">
              <Label htmlFor="from-user">From (who paid)</Label>
              <Select
                value={fromUserId}
                onValueChange={setFromUserId}
                disabled={isSubmitting}
              >
                <SelectTrigger id="from-user">
                  <SelectValue placeholder="Select who made the payment" />
                </SelectTrigger>
                <SelectContent>
                  {groupMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name} {member.id === currentUserId && "(You)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="to-user">To (who received)</Label>
              <Select
                value={toUserId}
                onValueChange={setToUserId}
                disabled={isSubmitting}
              >
                <SelectTrigger id="to-user">
                  <SelectValue placeholder="Select who received the payment" />
                </SelectTrigger>
                <SelectContent>
                  {groupMembers
                    .filter((member) => member.id !== fromUserId)
                    .map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name} {member.id === currentUserId && "(You)"}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Settlement Type Selection */}
            <div className="grid gap-2">
              <Label>Settlement Type</Label>
              <RadioGroup
                value={settlementType}
                onValueChange={(value) => setSettlementType(value as "full" | "partial")}
                disabled={isSubmitting}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="full" id="full" disabled={!currentDebt} />
                  <Label
                    htmlFor="full"
                    className={cn(
                      "font-normal cursor-pointer",
                      !currentDebt && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    Full Settlement
                    {currentDebt ? (
                      <span className="text-muted-foreground ml-2">
                        ({DEFAULT_CURRENCY} {currentDebt.amount.toFixed(2)})
                      </span>
                    ) : (
                      <span className="text-muted-foreground ml-2">
                        (No debt to settle)
                      </span>
                    )}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="partial" id="partial" />
                  <Label htmlFor="partial" className="font-normal cursor-pointer">
                    Partial Settlement / Custom Amount
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="amount">Amount</Label>
              <div className="flex gap-2">
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="flex-1"
                  disabled={isSubmitting || settlementType === "full"}
                />
                <Select
                  value={currency}
                  onValueChange={setCurrency}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_CODES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="e.g., Settling last month's expenses"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSubmitting}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Settlement"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}